import React, { useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, IconButton, Tooltip, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'

import { IOSSwitch } from '../IOSSwitch'
import { DefaultSelect } from '../DefaultSelect'
import { DefaultRadioGroup } from '../DefaultRadioGroup'

import { useLogic } from '../../hooks/storeHook'
import { useMessages } from '../../hooks/snackbarHooks'
import { useBoolState, useToggleState } from '../../hooks/commonHooks'
import { useSettingsPageStyles } from '../../hooks/settingsPageStylesHook'
import { useRadioGroupStyles } from '../../hooks/radioGroupStylesHook'

import { required } from '../../common/objectUtils'
import { valuesOfNumericEnum } from '../../common/tsUtils'

import { StoreAccountProps } from '../../types/accountProps'
import { commissionsRecordingTypeNames } from '../../types/commissionsRecordingTypeNames'

import { CommissionsRecordingType } from '../../server/mpsklad_core/Entity/Base/CommissionsRecordingType'
import { IntegrationType } from '../../server/mpsklad_core/Entity/IntegrationType'
import { WbCommissionCalculationMode } from '../../server/mpsklad_core/Entity/Base/WbCommissionCalculationMode'

const commissionRecordingOptions =
  valuesOfNumericEnum(CommissionsRecordingType)
    .map(value => ({value, label: commissionsRecordingTypeNames[value]}))

export const CommissionsSettings =
  observer(({account}: StoreAccountProps) => {
    const {showSuccess, showError} = useMessages()

    const logic = useLogic()

    const {palette} = useTheme()
    const radioGroupClasses = useRadioGroupStyles()

    const accountId = required(account.id)

    const isWb = account.integrationType === IntegrationType.Wildberries

    const [isLoading, setIsLoading, setNotLoading] = useBoolState()

    const [isUsingCommissions, toggleIsUsingCommissions] = useToggleState(account?.isUsingCommissions ?? false)

    const [ordersCommissionsRecording, setOrdersCommissionsRecording] =
      useState(account.ordersCommissionsRecording)

    const [documentsCommissionsRecording, setDocumentsCommissionsRecording] =
      useState(account.documentsCommissionsRecording)

    const [shipmentsCommissionsRecording, setShipmentsCommissionsRecording] =
      useState(account.shipmentsCommissionsRecording)

    const [commissionCalculationMode, setCommissionCalculationMode] =
      useState<WbCommissionCalculationMode>(
        isWb ? account.commissionCalculationMode
             : WbCommissionCalculationMode.DetailedDeductions)

    const wbCommissionModeOptions =
      useMemo(() => [{
        value: WbCommissionCalculationMode.DetailedDeductions,
        label: 'Учёт всех показателей с детализацией',
        hint: 'Сумма удержаний по строкам отчёта (логистика, эквайринг, вознаграждение WB с НДС и т.д.).'
      }, {
        value: WbCommissionCalculationMode.RetailMinusForPayWithLogistics,
        label: 'Учёт комиссий по формуле',
        hint: 'По строке: розничная цена со скидкой минус «к перечислению продавцу» плюс логистика (delivery_rub, rebill_logistic_cost), хранение (storage_fee), прочие удержания/выплаты (deduction), штрафы (penalty).'
      }], [])

    const classes = useSettingsPageStyles()

    const onSubmit = async () => {
      try {
        setIsLoading()

        await logic.setCommissionsSettings({
          accountId,
          integrationType: account.integrationType,
          isUsingCommissions,
          ordersCommissionsRecording,
          documentsCommissionsRecording,
          shipmentsCommissionsRecording,
          commissionCalculationMode: isWb ? commissionCalculationMode : undefined
        })

        showSuccess('Настройки комиссий обновлены!')
      } catch (e) {
        showError('Произошла ошибка при настройке комиссий!')
      } finally {
        setNotLoading()
      }
    }

    return (
      <Box className={classes.pageContainer}>
        <Typography className={classes.header}>
          НАСТРОЙКИ КОМИССИЙ
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={7}>
            <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
              <Grid item className={classes.root} xs={12}>
                <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                  <FormLabel className={classes.unifiedPricesLabel}>
                    Автоматический учёт комиссий
                  </FormLabel>

                  <IOSSwitch checked={isUsingCommissions} onChange={toggleIsUsingCommissions}/>
                </Box>

                <FormHelperText className={classes.helperText}>
                  Обновляется вместе с заказами
                </FormHelperText>
              </Grid>
            </Grid>
          </Grid>

          {
            isUsingCommissions &&
            <>
              {
                isWb &&
                <Grid item xs={7}>
                  <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                    <Grid item xs={12} className={classes.root}>
                      <DefaultRadioGroup
                        label={
                          <Box display="flex" alignItems="center" component="span">
                            Вариант учёта комиссий

                            <Tooltip
                              title="Первый вариант — как раньше. Второй — комиссия как разница цен по отчёту WB плюс логистика."
                              arrow>

                              <IconButton size="small" className={classes.helperIcon}>
                                <HelpOutlineIcon fontSize="small" style={{color: palette.mpSkladPrimary.blue}}/>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                        value={commissionCalculationMode}
                        onChange={setCommissionCalculationMode}
                        options={wbCommissionModeOptions}
                        getOptionClassName={
                          checked =>
                            clsx(radioGroupClasses.radioButton, checked && radioGroupClasses.selectedRadioButton)
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              }

              <Grid item xs={7}>
                <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                  <Grid item className={classes.root} xs={12}>
                    <Box>
                      <FormLabel component="legend" className={classes.label}>
                        <span className={classes.dash}>—</span>
                        Учитывать в документах
                      </FormLabel>
                    </Box>

                    <DefaultSelect
                      fullWidth
                      value={documentsCommissionsRecording}
                      options={commissionRecordingOptions}
                      onChange={value => setDocumentsCommissionsRecording(required(value))}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={7}>
                <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                  <Grid item className={classes.root} xs={12}>
                    <Box>
                      <FormLabel component="legend" className={classes.label}>
                        <span className={classes.dash}>—</span>
                        Учитывать в заказах
                      </FormLabel>
                    </Box>

                    <DefaultSelect
                      fullWidth
                      value={ordersCommissionsRecording}
                      options={commissionRecordingOptions}
                      onChange={value => setOrdersCommissionsRecording(required(value))}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={7}>
                <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                  <Grid item className={classes.root} xs={12}>
                    <Box>
                      <FormLabel component="legend" className={classes.label}>
                        <span className={classes.dash}>—</span>
                        Учитывать в отгрузках
                      </FormLabel>
                    </Box>

                    <DefaultSelect
                      fullWidth
                      value={shipmentsCommissionsRecording}
                      options={commissionRecordingOptions}
                      onChange={value => setShipmentsCommissionsRecording(required(value))}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          }
        </Grid>

        <Box display="flex">
          <button
            className={clsx(classes.saveButton, 'default-button')}
            disabled={isLoading}
            onClick={onSubmit}>
            <p>{isLoading ? 'Сохраняем...' : 'Сохранить'}</p>
          </button>
        </Box>
      </Box>
    )
  })